import React, { useEffect, useState, useCallback } from 'react';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import makeAnimated from 'react-select/animated';
import { host } from '../script/variables.js';
import uploadViaPresign from '../script/uploadViaPresignV2';

const dialcodelist = require('../script/countrydialcode.js');
const animatedComponents = makeAnimated();

/* ─────────────────────────────────────────────
   Google Font — Instrument Serif + DM Mono
───────────────────────────────────────────────*/
if (typeof document !== 'undefined' && !document.getElementById('up-fonts')) {
  const link = document.createElement('link');
  link.id = 'up-fonts';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Mono:wght@400;500&family=Geist:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
}

/* ─────────────────────────────────────────────
   Global styles injection
───────────────────────────────────────────────*/
if (typeof document !== 'undefined' && !document.getElementById('up-global-styles')) {
  const style = document.createElement('style');
  style.id = 'up-global-styles';
  style.textContent = `
    @keyframes up-fade-up {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes up-fade-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes up-shimmer {
      0%   { background-position: -200% center; }
      100% { background-position:  200% center; }
    }
    @keyframes up-pulse-ring {
      0%   { transform: scale(1);   opacity: 0.6; }
      100% { transform: scale(1.6); opacity: 0;   }
    }
    @keyframes up-success-pop {
      0%   { transform: scale(0.7); opacity: 0; }
      70%  { transform: scale(1.05); }
      100% { transform: scale(1);   opacity: 1; }
    }
    @keyframes up-check-draw {
      from { stroke-dashoffset: 40; }
      to   { stroke-dashoffset: 0;  }
    }
    .up-step-enter { animation: up-fade-up 0.35s cubic-bezier(0.22,1,0.36,1) both; }
    .up-input:focus { outline: none; border-color: #111 !important; box-shadow: 0 0 0 3px rgba(0,0,0,0.08); }
    .up-otp-single:focus { outline: none; border-color: #111 !important; box-shadow: 0 0 0 3px rgba(0,0,0,0.08); }
    .up-gender-btn:hover { border-color: #111 !important; }
    .up-nav-btn-primary:hover:not(:disabled) { background: #222 !important; transform: translateY(-1px); box-shadow: 0 4px 16px rgba(0,0,0,0.18); }
    .up-nav-btn-secondary:hover { background: #f5f5f5 !important; }
    .up-skip-btn:hover { color: #111 !important; }
    .up-file-label:hover { border-color: #111 !important; }
    .up-upload-btn:hover:not(:disabled) { background: #111 !important; color: #fff !important; }
    .up-progress-dot { transition: all 0.4s cubic-bezier(0.34,1.56,0.64,1); }

    /* Responsive */
    @media (max-width: 600px) {
      .up-root { padding: 0 !important; align-items: flex-end !important; }
      .up-card { border-radius: 24px 24px 0 0 !important; max-width: 100% !important;  padding: 32px 24px 40px !important; }
      .up-sidebar { display: none !important; }
      .up-layout { flex-direction: column !important; }
    }
    @media (min-width: 601px) and (max-width: 900px) {
      .up-sidebar { display: none !important; }
      .up-card { max-width: 520px !important; }
    }
    @media (min-width: 901px) {
      .up-layout { gap: 40px; }
    }
  `;
  document.head.appendChild(style);
}

/* ─────────────────────────────────────────────
   Select styles — crisp B&W
───────────────────────────────────────────────*/
const selectStyles = {
  control: (base, state) => ({
    ...base,
    background: '#fff',
    borderColor: state.isFocused ? '#111' : '#e0e0e0',
    borderRadius: '10px',
    padding: '2px 4px',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(0,0,0,0.08)' : 'none',
    borderWidth: '1.5px',
    '&:hover': { borderColor: '#111' },
    fontFamily: "'Geist', sans-serif",
    fontSize: '14px',
  }),
  menu: (base) => ({
    ...base,
    background: '#fff',
    borderRadius: '12px',
    border: '1.5px solid #e8e8e8',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    overflow: 'hidden',
  }),
  option: (base, state) => ({
    ...base,
    background: state.isFocused ? '#f5f5f5' : 'transparent',
    color: '#111',
    borderRadius: '6px',
    margin: '2px 4px',
    width: 'calc(100% - 8px)',
    fontFamily: "'Geist', sans-serif",
    fontSize: '14px',
    cursor: 'pointer',
  }),
  singleValue: (base) => ({ ...base, color: '#111', fontFamily: "'Geist', sans-serif" }),
  multiValue: (base) => ({ ...base, background: '#f0f0f0', borderRadius: '6px' }),
  multiValueLabel: (base) => ({ ...base, color: '#111', fontFamily: "'Geist', sans-serif", fontSize: '13px' }),
  multiValueRemove: (base) => ({ ...base, color: '#666', '&:hover': { background: '#e0e0e0', color: '#111' } }),
  placeholder: (base) => ({ ...base, color: '#aaa', fontFamily: "'Geist', sans-serif", fontSize: '14px' }),
  input: (base) => ({ ...base, color: '#111', fontFamily: "'Geist', sans-serif" }),
};

/* ─────────────────────────────────────────────
   FocusInput
───────────────────────────────────────────────*/
const FocusInput = ({ style, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      className="up-input"
      style={{
        width: '100%',
        background: '#fff',
        border: `1.5px solid ${focused ? '#111' : '#e0e0e0'}`,
        borderRadius: '10px',
        padding: '13px 16px',
        fontSize: '15px',
        color: '#111',
        outline: 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxSizing: 'border-box',
        fontFamily: "'Geist', sans-serif",
        ...style,
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

/* ─────────────────────────────────────────────
   Step indicator sidebar
───────────────────────────────────────────────*/
const Sidebar = ({ steps, curIndex, completed }) => (
  <div className="up-sidebar" style={{
    width: '200px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    paddingTop: '8px',
  }}>
    <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#aaa', marginBottom: '12px', fontFamily: "'Geist', sans-serif" }}>
      Setup Steps
    </p>
    {steps.slice(0, -1).map((step, i) => {
      const done   = completed.has(i);
      const active = i === curIndex;
      return (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '8px', background: active ? '#f5f5f5' : 'transparent', transition: 'background 0.2s' }}>
          <div style={{
            width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: done ? '#111' : active ? '#fff' : 'transparent',
            border: `1.5px solid ${done ? '#111' : active ? '#111' : '#ddd'}`,
            transition: 'all 0.25s',
          }}>
            {done ? (
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 5l2.5 2.5L8 3" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <span style={{ fontSize: '10px', fontWeight: 600, color: active ? '#111' : '#bbb', fontFamily: "'DM Mono', monospace" }}>{i + 1}</span>
            )}
          </div>
          <span style={{ fontSize: '13px', fontWeight: active ? 600 : 400, color: active ? '#111' : done ? '#555' : '#bbb', fontFamily: "'Geist', sans-serif", transition: 'color 0.2s' }}>
            {step.shortTitle || step.title?.split(' ').slice(0, 3).join(' ')}
          </span>
        </div>
      );
    })}
  </div>
);

/* ─────────────────────────────────────────────
   Hint line
───────────────────────────────────────────────*/
const Hint = ({ ok, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', fontSize: '12.5px', color: ok ? '#16a34a' : '#dc2626', fontFamily: "'Geist', sans-serif", fontWeight: 500 }}>
    <div style={{ width: '14px', height: '14px', borderRadius: '50%', background: ok ? '#16a34a' : '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ color: '#fff', fontSize: '9px', fontWeight: 700 }}>{ok ? '✓' : '✗'}</span>
    </div>
    {children}
  </div>
);

/* ─────────────────────────────────────────────
   Field label
───────────────────────────────────────────────*/
const Label = ({ children }) => (
  <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888', marginBottom: '8px', fontFamily: "'Geist', sans-serif" }}>
    {children}
  </label>
);

/* ═════════════════════════════════════════════
   Main Component
═════════════════════════════════════════════ */
const UpdateProfile = ({ userData }) => {
  const [curIndex, setCurIndex]         = useState(0);
  const [isNewDetails, setIsNewDetails] = useState(false);
  const [animKey, setAnimKey]           = useState(0);
  const [completedSteps, setCompletedSteps] = useState(() => new Set());
  const [skippedSteps, setSkippedSteps]     = useState(() => new Set());

  /* — Field states — */
  const [username, setUsername]                   = useState('');
  const [isUsernameAv, setUsernameAv]             = useState(null);
  const [instausername, setInstaUsername]         = useState('');
  const [twitterurl, setTwitterUrl]               = useState('');
  const [isValidTwitterUrl, setValidTwitterUrl]   = useState(null);
  const [linkedinurl, setLinkedinUrl]             = useState('');
  const [isLinkedinValid, setLinkedinValid]       = useState(null);
  const [profilepicurl, setProfilePicUrl]         = useState(null);
  const [profilePicFile, setProfilePicFile]       = useState(null);
  const [isFileUploading, setFileUploading]       = useState(false);
  const [isPicUploaded, setIsPicUploaded]         = useState(false);
  const [gender, setGender]                       = useState('');
  const [countryCode, setCountrycode]             = useState(null);
  const [phno, setPhno]                           = useState('');
  const [otpSent, setOtpSent]                     = useState(false);
  const [otpValue, setOtpValue]                   = useState('');   // ← single string now
  const [verified, setVerified]                   = useState(false);
  const [otpLoading, setOtpLoading]               = useState(false);
  const [otpError, setOtpError]                   = useState('');
  const [otpCooldown, setOtpCooldown]             = useState(0);
  const [favFood, setFavFood]                     = useState([]);
  const [dob, setDob]                             = useState('');

  /* ── Cooldown timer ── */
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setTimeout(() => setOtpCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [otpCooldown]);

  /* ── Username availability check (debounced) ── */
  useEffect(() => {
    if (!username) { setUsernameAv(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${host}/user/profile/update/unamecheck?uname=${username}`);
        const d = await r.json();
        setUsernameAv(d.length === 0);
      } catch { setUsernameAv(null); }
    }, 500);
    return () => clearTimeout(t);
  }, [username]);

  const fetchDishName = useCallback(async (inp) => {
    try {
      const r = await fetch(`${host}/masala/fetchDishes?dname=${inp}`);
      if (r.ok) {
        const d = await r.json();
        return d.map(item => ({ value: item.name, label: item.name }));
      }
    } catch { return []; }
    return [];
  }, []);

  const token = () => localStorage.getItem('token');

  const updateAttribute = async (attribute, value) => {
    try {
      const r = await fetch(`${host}/user/update/profile1`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token() },
        body: JSON.stringify({ attribute, value }),
      });
      if (r.ok) { setIsNewDetails(true); userData?.(); return true; }
    } catch { /* fall through */ }
    return false;
  };

  /* ── OTP: send ── */
  const sendOTP = async () => {
    if (!phno || phno.length < 10) return;
    setOtpLoading(true); setOtpError('');
    try {
      const r = await fetch(`${host}/api/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: `${countryCode?.value || ''}${phno}` }),
      });
      const d = await r.json();
      if (r.ok) { setOtpSent(true); setOtpCooldown(60); }
      else setOtpError(d.message || 'Failed to send OTP.');
    } catch { setOtpError('Network error. Please try again.'); }
    finally { setOtpLoading(false); }
  };

  /* ── OTP: verify ── */
  const verifyOTP = async () => {
    if (otpValue.length !== 6) return;
    setOtpLoading(true); setOtpError('');
    try {
      const r = await fetch(`${host}/api/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: `${countryCode?.value || ''}${phno}`, otp: otpValue }),
      });
      const d = await r.json();
      if (r.ok && d.verified) { setVerified(true); }
      else { setOtpError(d.message || 'Invalid OTP.'); setOtpValue(''); }
    } catch { setOtpError('Network error. Please try again.'); }
    finally { setOtpLoading(false); }
  };

  /* ── S3 upload ── */
  const uploadFile = async () => {
    if (!profilePicFile) return;
    if (!['image/jpeg', 'image/png'].includes(profilePicFile.type)) {
      alert('Please choose a JPEG or PNG file.');
      return;
    }
    setFileUploading(true);
    try {
      const imgUrl = await uploadViaPresign(profilePicFile, 'moment');
      await updateAttribute('image', imgUrl);
      setIsPicUploaded(true);
      setCompletedSteps(prev => new Set(prev).add(4));
    } catch (err) { alert('Upload failed: ' + err.message); }
    finally { setFileUploading(false); }
  };

  /* ─────────────────────────────────────────────
     Whether the current step has a *valid* value ready to save.
     Empty/untouched fields are always allowed through (they're
     simply skipped), so this only blocks on active validation
     errors — e.g. a taken username or a malformed URL.
  ───────────────────────────────────────────────*/
  const canProceed = () => {
    switch (curIndex) {
      case 0: return username === '' || isUsernameAv === true;
      case 1: return true; // instagram — always optional
      case 2: return twitterurl === '' || isValidTwitterUrl === true;
      case 3: return linkedinurl === '' || isLinkedinValid === true;
      case 4: return !profilePicFile || isPicUploaded; // no file chosen, or already uploaded
      case 5: return true; // gender — optional
      case 6: return true; // country — optional
      case 7: return phno === '' || verified; // phone typed must be verified, else leave blank
      case 8: return true; // fav dishes — optional
      case 9: return true; // dob — optional
      default: return true;
    }
  };

  /* Does the current step actually have data worth saving? */
  const hasDataToSave = () => {
    switch (curIndex) {
      case 0: return username !== '' && isUsernameAv === true;
      case 1: return instausername !== '';
      case 2: return twitterurl !== '' && isValidTwitterUrl === true;
      case 3: return linkedinurl !== '' && isLinkedinValid === true;
      case 5: return gender !== '';
      case 6: return countryCode !== null;
      case 7: return phno !== '' && verified;
      case 8: return favFood.length > 0;
      case 9: return dob !== '';
      default: return false;
    }
  };

  const navigate = (dir) => {
    setAnimKey(k => k + 1);
    setCurIndex(i => i + dir);
  };

  /* Move to the next step without saving anything from this one */
  const handleSkip = () => {
    setSkippedSteps(prev => new Set(prev).add(curIndex));
    setCompletedSteps(prev => {
      if (!prev.has(curIndex)) return prev;
      const next = new Set(prev);
      next.delete(curIndex);
      return next;
    });
    if (curIndex < steps.length - 1) navigate(1);
  };

  const handleNext = async () => {
    const attrMap = {
      0: ['username', username],
      1: ['insta', instausername],
      2: ['twitter', twitterurl],
      3: ['linkedin', linkedinurl],
      5: ['gender', gender],
      6: ['country', countryCode?.value],
      7: ['phone', `${countryCode?.value || ''}${phno}`],
      8: ['favdishes', favFood],
      9: ['dob', dob],
    };

    if (hasDataToSave() && attrMap[curIndex]) {
      const ok = await updateAttribute(...attrMap[curIndex]);
      if (ok) {
        setCompletedSteps(prev => new Set(prev).add(curIndex));
        setSkippedSteps(prev => {
          if (!prev.has(curIndex)) return prev;
          const next = new Set(prev);
          next.delete(curIndex);
          return next;
        });
      }
    } else {
      // Nothing entered for this step — treat it like a skip so the
      // user can still move forward and finish with partial details.
      setSkippedSteps(prev => new Set(prev).add(curIndex));
    }

    if (curIndex < steps.length - 1) navigate(1);
  };

  const handleTwitterUrl = (url) => {
    setTwitterUrl(url);
    setValidTwitterUrl(url === '' ? null : /^https:\/\/(www\.)?twitter\.com\/(#!\/)?[a-zA-Z0-9_]+$/i.test(url));
  };

  const handleLinkedinUrl = (url) => {
    setLinkedinUrl(url);
    setLinkedinValid(url === '' ? null : /(?:https?:\/\/)?(?:[\w]+\.)?linkedin\.com\/in\/([\w\-\_À-ÿ%]+)\/?/i.test(url));
  };

  /* ─────────────────────────────────────────────
     Steps definition
  ───────────────────────────────────────────────*/
  const steps = [
    {
      shortTitle: 'Username',
      title: "Pick your username",
      sub: "This is how others discover you on the platform.",
      optional: true,
      content: (
        <>
          <Label>Username</Label>
          <FocusInput value={username} onChange={e => setUsername(e.target.value.toLowerCase().replace(/\s/g, '_'))} placeholder="e.g. foodie_raj" />
          {username && isUsernameAv !== null && (
            <Hint ok={isUsernameAv}>{isUsernameAv ? 'Username is available' : 'That username is already taken'}</Hint>
          )}
          {!username && (
            <p style={{ fontSize: '12px', color: '#aaa', marginTop: '8px', fontFamily: "'Geist', sans-serif" }}>Only lowercase letters, numbers and underscores</p>
          )}
        </>
      ),
    },
    {
      shortTitle: 'Instagram',
      title: "Instagram handle",
      sub: "Connect your social presence.",
      optional: true,
      content: (
        <>
          <Label>Instagram username</Label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontFamily: "'Geist', sans-serif", fontSize: '15px', pointerEvents: 'none' }}>@</span>
            <FocusInput value={instausername} onChange={e => setInstaUsername(e.target.value)} placeholder="yourhandle" style={{ paddingLeft: '30px' }} />
          </div>
        </>
      ),
    },
    {
      shortTitle: 'Twitter',
      title: "Twitter profile",
      sub: "Share your Twitter presence.",
      optional: true,
      content: (
        <>
          <Label>Twitter URL</Label>
          <FocusInput value={twitterurl} onChange={e => handleTwitterUrl(e.target.value)} placeholder="https://twitter.com/yourhandle" />
          {twitterurl && isValidTwitterUrl !== null && (
            <Hint ok={isValidTwitterUrl}>{isValidTwitterUrl ? 'Valid Twitter URL' : 'URL format: twitter.com/yourhandle'}</Hint>
          )}
        </>
      ),
    },
    {
      shortTitle: 'LinkedIn',
      title: "LinkedIn profile",
      sub: "Your professional networking presence.",
      optional: true,
      content: (
        <>
          <Label>LinkedIn URL</Label>
          <FocusInput value={linkedinurl} onChange={e => handleLinkedinUrl(e.target.value)} placeholder="https://linkedin.com/in/yourname" />
          {linkedinurl && isLinkedinValid !== null && (
            <Hint ok={isLinkedinValid}>{isLinkedinValid ? 'Valid LinkedIn URL' : 'URL format: linkedin.com/in/yourname'}</Hint>
          )}
        </>
      ),
    },
    {
      shortTitle: 'Photo',
      title: "Profile photo",
      sub: "Put a face to your name.",
      optional: true,
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
          <div style={{ position: 'relative' }}>
            {profilepicurl ? (
              <img src={profilepicurl} alt="preview" style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #e0e0e0' }} />
            ) : (
              <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: '#f5f5f5', border: '2px dashed #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '36px' }}>
                🧑
              </div>
            )}
            {isPicUploaded && (
              <div style={{ position: 'absolute', bottom: 0, right: 0, width: '28px', height: '28px', borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff', animation: 'up-success-pop 0.4s ease both' }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="40" strokeDashoffset="0" style={{ animation: 'up-check-draw 0.3s 0.1s ease both' }} />
                </svg>
              </div>
            )}
          </div>

          <label className="up-file-label" style={{ background: '#fff', border: '1.5px solid #e0e0e0', borderRadius: '10px', padding: '11px 20px', color: '#555', fontSize: '13px', cursor: 'pointer', fontWeight: 500, fontFamily: "'Geist', sans-serif", transition: 'border-color 0.15s', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M7.5 1v9M4 5l3.5-4L11 5M2 12h11" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Choose photo
            <input type="file" accept="image/jpeg,image/png" style={{ display: 'none' }} onChange={e => {
              const file = e.target.files[0];
              if (!file) return;
              setProfilePicFile(file);
              setIsPicUploaded(false);
              const r = new FileReader();
              r.onloadend = () => setProfilePicUrl(r.result);
              r.readAsDataURL(file);
            }} />
          </label>

          {profilepicurl && !isPicUploaded && (
            <button
              className="up-upload-btn"
              onClick={uploadFile}
              disabled={isFileUploading}
              style={{ background: isFileUploading ? '#f0f0f0' : '#fff', color: isFileUploading ? '#aaa' : '#111', border: '1.5px solid #e0e0e0', borderRadius: '10px', padding: '11px 24px', fontSize: '13px', fontWeight: 600, cursor: isFileUploading ? 'not-allowed' : 'pointer', fontFamily: "'Geist', sans-serif", transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {isFileUploading ? (
                <><span style={{ width: '14px', height: '14px', borderRadius: '50%', border: '2px solid #ddd', borderTopColor: '#aaa', display: 'inline-block', animation: 'up-spin 0.7s linear infinite' }} /> Uploading…</>
              ) : 'Upload Photo'}
            </button>
          )}
        </div>
      ),
    },
    {
      shortTitle: 'Gender',
      title: "Your gender",
      sub: "Help us personalise your experience.",
      optional: true,
      content: (
        <>
          <Label>Select gender</Label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginTop: '4px' }}>
            {[
              { value: 'male',   emoji: '👨', label: 'Male'   },
              { value: 'female', emoji: '👩', label: 'Female' },
              { value: 'other',  emoji: '🧑', label: 'Other'  },
            ].map(g => (
              <button
                key={g.value}
                className="up-gender-btn"
                onClick={() => setGender(g.value)}
                style={{
                  padding: '16px 8px',
                  borderRadius: '12px',
                  border: `1.5px solid ${gender === g.value ? '#111' : '#e0e0e0'}`,
                  background: gender === g.value ? '#f5f5f5' : '#fff',
                  color: gender === g.value ? '#111' : '#777',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.18s',
                  fontFamily: "'Geist', sans-serif",
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                }}
              >
                <span style={{ fontSize: '24px' }}>{g.emoji}</span>
                {g.label}
              </button>
            ))}
          </div>
        </>
      ),
    },
    {
      shortTitle: 'Country',
      title: "Your country",
      sub: "For the correct dialling code.",
      optional: true,
      content: (
        <>
          <Label>Country</Label>
          <Select
            styles={selectStyles}
            closeMenuOnSelect
            components={animatedComponents}
            value={countryCode}
            onChange={setCountrycode}
            placeholder="Select country…"
            options={dialcodelist}
          />
        </>
      ),
    },
    {
      shortTitle: 'Phone',
      title: "Verify your phone",
      sub: "We'll send a 6-digit OTP to confirm.",
      optional: true,
      content: (
        <>
          <Label>Mobile number</Label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <FocusInput
              value={phno}
              onChange={e => {
                setPhno(e.target.value.replace(/\D/g, ''));
                setVerified(false);
                setOtpSent(false);
                setOtpValue('');
              }}
              placeholder="10-digit number"
              maxLength={10}
              style={{ flex: 1 }}
            />
            <button
              onClick={sendOTP}
              disabled={phno.length !== 10 || otpLoading || otpCooldown > 0}
              style={{
                background: phno.length === 10 && otpCooldown === 0 ? '#111' : '#f0f0f0',
                color: phno.length === 10 && otpCooldown === 0 ? '#fff' : '#aaa',
                border: 'none',
                borderRadius: '10px',
                padding: '0 16px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: phno.length === 10 && otpCooldown === 0 ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap',
                fontFamily: "'Geist', sans-serif",
                transition: 'all 0.2s',
                flexShrink: 0,
              }}
            >
              {otpLoading ? '…' : otpCooldown > 0 ? `${otpCooldown}s` : otpSent ? 'Resend' : 'Send OTP'}
            </button>
          </div>

          {/* ── Single OTP input field ── */}
          {otpSent && !verified && (
            <div style={{ marginTop: '24px', animation: 'up-fade-up 0.3s ease both' }}>
              <Label>Enter 6-digit OTP</Label>
              <input
                className="up-otp-single"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otpValue}
                placeholder="••••••"
                autoFocus
                onChange={e => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setOtpValue(val);
                  setOtpError('');
                }}
                style={{
                  width: '100%',
                  background: '#fff',
                  border: `1.5px solid ${otpError ? '#dc2626' : otpValue.length === 6 ? '#111' : '#e0e0e0'}`,
                  borderRadius: '10px',
                  padding: '14px 18px',
                  fontSize: '24px',
                  fontWeight: 700,
                  color: '#111',
                  textAlign: 'center',
                  outline: 'none',
                  letterSpacing: '0.35em',
                  fontFamily: "'DM Mono', monospace",
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                  boxSizing: 'border-box',
                }}
              />
              <button
                onClick={verifyOTP}
                disabled={otpValue.length !== 6 || otpLoading}
                style={{
                  width: '100%',
                  marginTop: '12px',
                  background: otpValue.length === 6 ? '#111' : '#f0f0f0',
                  color: otpValue.length === 6 ? '#fff' : '#aaa',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '13px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: otpValue.length === 6 ? 'pointer' : 'not-allowed',
                  fontFamily: "'Geist', sans-serif",
                  transition: 'all 0.2s',
                }}
              >
                {otpLoading ? 'Verifying…' : 'Verify OTP'}
              </button>
            </div>
          )}

          {otpError && <Hint ok={false}>{otpError}</Hint>}
          {verified && (
            <div style={{ marginTop: '14px', display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', color: '#16a34a', fontWeight: 500, fontFamily: "'Geist', sans-serif", animation: 'up-success-pop 0.4s ease both' }}>
              <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5L8 3" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
              Phone verified successfully
            </div>
          )}
        </>
      ),
    },
    {
      shortTitle: 'Fav dishes',
      title: "Favourite dishes",
      sub: "Tell us what you love to eat.",
      optional: true,
      content: (
        <>
          <Label>Search dishes</Label>
          <AsyncSelect
            styles={selectStyles}
            closeMenuOnSelect={false}
            components={animatedComponents}
            value={favFood}
            onChange={setFavFood}
            loadOptions={fetchDishName}
            placeholder="Type to search dishes…"
            isMulti
          />
          {favFood.length > 0 && (
            <p style={{ fontSize: '12px', color: '#888', marginTop: '8px', fontFamily: "'Geist', sans-serif" }}>{favFood.length} dish{favFood.length !== 1 ? 'es' : ''} selected</p>
          )}
        </>
      ),
    },
    {
      shortTitle: 'Birthday',
      title: "Date of birth",
      sub: "We'll celebrate with you! 🎂",
      optional: true,
      content: (
        <>
          <Label>Date of birth</Label>
          <FocusInput type="date" value={dob} onChange={e => setDob(e.target.value)} max={new Date().toISOString().split('T')[0]} />
        </>
      ),
    },
    {
      title: null,
      content: (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <div style={{ fontSize: '56px', marginBottom: '20px', animation: 'up-success-pop 0.5s ease both' }}>
            {isNewDetails ? '🎉' : '📋'}
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#111', marginBottom: '10px', fontFamily: "'Instrument Serif', serif" }}>
            {isNewDetails ? (completedSteps.size < 9 ? 'Profile partially updated!' : 'Profile updated!') : 'No changes made'}
          </h2>
          <p style={{ fontSize: '14px', color: '#888', lineHeight: 1.6, fontFamily: "'Geist', sans-serif", maxWidth: '300px', margin: '0 auto' }}>
            {isNewDetails
              ? (completedSteps.size < 9
                  ? `You saved ${completedSteps.size} of 9 details. You can fill in the rest anytime from settings.`
                  : 'Your profile looks great. Start exploring the platform.')
              : 'You skipped all steps. You can update your profile anytime from settings.'}
          </p>
        </div>
      ),
    },
  ];

  const isLast = curIndex === steps.length - 1;
  const step   = steps[curIndex];
  const totalSteps = steps.length - 1;
  const progress = ((curIndex) / (totalSteps - 1)) * 100;

  return (
    <div className="up-root" style={{
      minHeight: '0vh',
      background: '#fafafa',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Geist', sans-serif",
      padding: '24px 16px',
    }}>
      <div className="up-layout" style={{
        display: 'flex',
        alignItems: 'flex-start',
        width: '100%',
        maxWidth: '820px',
        gap: '32px',
      }}>

        {/* ── Sidebar (desktop only) ── */}
        <Sidebar steps={steps} curIndex={curIndex} completed={completedSteps} />

        {/* ── Main card ── */}
        <div className="up-card" style={{
          flex: 1,
          background: '#fff',
          borderRadius: '20px',
          padding: '40px 40px 36px',
          boxShadow: '0 2px 24px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)',
          border: '1px solid #ebebeb',
          position: 'relative',
          overflow: 'hidden',
        }}>

          {/* Progress bar */}
          {!isLast && (
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#f0f0f0' }}>
              <div style={{ height: '100%', width: `${progress}%`, background: '#111', borderRadius: '0 2px 2px 0', transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)' }} />
            </div>
          )}

          {/* Step counter + dots */}
          {!isLast && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '32px' }}>
              <div style={{ display: 'flex', gap: '4px', flex: 1 }}>
                {steps.slice(0, -1).map((_, i) => (
                  <div
                    key={i}
                    className="up-progress-dot"
                    style={{
                      height: '3px',
                      flex: i === curIndex ? 2 : 1,
                      borderRadius: '2px',
                      background: i < curIndex ? '#111' : i === curIndex ? '#555' : '#e8e8e8',
                    }}
                  />
                ))}
              </div>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#aaa', fontFamily: "'DM Mono', monospace", marginLeft: '8px', flexShrink: 0 }}>
                {curIndex + 1}/{totalSteps}
              </span>
            </div>
          )}

          {/* Step content — animated */}
          <div key={animKey} className="up-step-enter">

            {/* Heading */}
            {step.title && (
              <>
                <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#111', marginBottom: '6px', lineHeight: 1.3, fontFamily: "'Instrument Serif', serif", letterSpacing: '-0.01em' }}>
                  {step.title}
                </h2>
                <p style={{ fontSize: '14px', color: '#888', marginBottom: '28px', lineHeight: 1.5, fontFamily: "'Geist', sans-serif" }}>
                  {step.sub}
                  {step.optional && <span style={{ marginLeft: '6px', fontSize: '12px', background: '#f5f5f5', color: '#aaa', padding: '2px 7px', borderRadius: '4px', fontWeight: 500 }}>Optional</span>}
                </p>
              </>
            )}

            {/* Content */}
            {step.content}

          </div>

          {/* ── Navigation ── */}
          {!isLast && (
            <div style={{ display: 'flex', gap: '10px', marginTop: '32px', alignItems: 'center' }}>
              {curIndex > 0 && (
                <button
                  className="up-nav-btn-secondary"
                  onClick={() => navigate(-1)}
                  style={{ padding: '13px 18px', borderRadius: '10px', border: '1.5px solid #e0e0e0', background: '#fff', color: '#555', fontSize: '14px', fontWeight: 500, cursor: 'pointer', fontFamily: "'Geist', sans-serif", transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke="#555" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  Back
                </button>
              )}

              <button
                className="up-skip-btn"
                onClick={handleSkip}
                style={{ padding: '13px 10px', background: 'transparent', border: 'none', color: '#aaa', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: "'Geist', sans-serif", transition: 'color 0.15s' }}
              >
                Skip
              </button>

              <button
                className="up-nav-btn-primary"
                onClick={handleNext}
                disabled={!canProceed()}
                title={!canProceed() ? 'Fix the highlighted issue, or tap Skip to move on' : undefined}
                style={{
                  flex: 1,
                  padding: '13px 20px',
                  borderRadius: '10px',
                  border: 'none',
                  background: canProceed() ? '#111' : '#f0f0f0',
                  color: canProceed() ? '#fff' : '#bbb',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: canProceed() ? 'pointer' : 'not-allowed',
                  fontFamily: "'Geist', sans-serif",
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                {hasDataToSave() ? 'Save & Continue' : 'Continue'}
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 2l5 5-5 5" stroke={canProceed() ? '#fff' : '#bbb'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateProfile;